import { z } from 'zod'

/**
 * Conditional requirements (design 15.2 m13: "Corporate bookings must have a PO number"; F 15.3-03). A CRM field
 * or a task template's required information can be required only when another field (or item) has a value:
 * `{ field: 'type', op: 'equals', value: 'Corporate' }`. The record validator, the task completeness check, the
 * AI's writes (which go through the same validator) and the designers all use these helpers.
 */
const Scalar = z.union([z.string(), z.number(), z.boolean()])
export const RequiredWhen = z.object({
  /** The other field's key (or the other required-information key, on a task template). */
  field: z.string().regex(/^[a-z][a-z0-9_]{0,62}$/),
  op: z.enum(['equals', 'not_equals', 'in', 'filled']).default('equals'),
  value: Scalar.optional(),
  values: z.array(Scalar).min(1).optional(),
}).refine((c) => (c.op === 'equals' || c.op === 'not_equals' ? c.value !== undefined : c.op === 'in' ? Boolean(c.values?.length) : true), {
  message: 'Say which value makes it required', path: ['value'],
})
export type RequiredWhen = z.infer<typeof RequiredWhen>

const norm = (v: unknown) => (typeof v === 'string' ? v.trim().toLowerCase() : typeof v === 'number' || typeof v === 'boolean' ? String(v).toLowerCase() : '')
/** Empty: missing, null, blank text or an empty list. */
export const isEmptyValue = (v: unknown) => v === undefined || v === null || (typeof v === 'string' && !v.trim()) || (Array.isArray(v) && v.length === 0)

/** Whether the condition holds for these values (case and spacing ignored; a list matches if any item does). */
export function conditionMet(c: RequiredWhen, data: Record<string, unknown>): boolean {
  const v = data?.[c.field]
  const vals = Array.isArray(v) ? v.map(norm) : [norm(v)]
  switch (c.op) {
    case 'filled': return !isEmptyValue(v)
    case 'equals': return vals.includes(norm(c.value))
    case 'not_equals': return !isEmptyValue(v) && !vals.includes(norm(c.value))
    case 'in': return (c.values ?? []).some((x) => vals.includes(norm(x)))
  }
}

/** "Type is Corporate", "Type is one of Corporate, Group", "Type is filled in". */
export function describeCondition(c: RequiredWhen, labelOf: (key: string) => string = (k) => k.replace(/_/g, ' ')): string {
  const l = labelOf(c.field)
  switch (c.op) {
    case 'filled': return `${l} is filled in`
    case 'equals': return `${l} is ${String(c.value)}`
    case 'not_equals': return `${l} is not ${String(c.value)}`
    case 'in': return `${l} is one of ${(c.values ?? []).map(String).join(', ')}`
  }
}

/** Fields whose condition holds but which are empty, as validation errors. */
export function conditionalRequiredErrors(fields: { key: string; label: string; required_when?: RequiredWhen }[], data: Record<string, unknown>) {
  const labelOf = (k: string) => fields.find((f) => f.key === k)?.label ?? k.replace(/_/g, ' ')
  return fields
    .filter((f) => f.required_when && conditionMet(f.required_when, data) && isEmptyValue(data?.[f.key]))
    .map((f) => ({ field: f.key, message: `A value is needed when ${describeCondition(f.required_when!, labelOf).replace(/^./, (x) => x.toLowerCase())}` }))
}

/**
 * Problems with the conditions themselves (designers and change sets): the other field must exist and not be the
 * field itself, and a choice field's value must be one of its options.
 */
export function requiredWhenIssues(fields: { key: string; label?: string; type?: string; options?: string[]; required_when?: RequiredWhen }[]): string[] {
  const out: string[] = []
  for (const f of fields) {
    const c = f.required_when
    if (!c) continue
    const other = fields.find((x) => x.key === c.field)
    if (c.field === f.key) { out.push(`${f.key}: it cannot be required when it is itself filled in`); continue }
    if (!other) { out.push(`${f.key}: "required when" names ${c.field}, which is not one of the fields`); continue }
    const wanted = c.op === 'in' ? c.values ?? [] : c.op === 'filled' ? [] : [c.value]
    if ((other.type === 'select' || other.type === 'multi_select') && other.options?.length) {
      const bad = wanted.filter((v) => !other.options!.some((o) => norm(o) === norm(v)))
      if (bad.length) out.push(`${f.key}: "${bad.map(String).join('", "')}" is not an option of ${other.label ?? other.key}`)
    }
  }
  return out
}
